// MUI から Box、Button、Grid、Typography コンポーネントをインポート
import { Box, Button, Grid, Typography } from '@mui/material';
import type { ColDef, ColGroupDef, GetContextMenuItems, GetContextMenuItemsParams, RowDoubleClickedEvent } from 'ag-grid-community';
// React から useState フックをインポート
import { useEffect, useState } from 'react';
// react-i18next から useTranslation フックをインポート
import { useTranslation } from 'react-i18next';
import { StyledAgGrid } from 'src/components/app-components/StyledAgGrid';
import ThreeDotMenuRenderer from 'src/components/app-components/ThreeDotMenuRenderer';
import { LoadableComponent } from 'src/components/app-components/bonsai/LoadableComponent';
import { CREATE_USER_SYSTEM, REMINDER_STATUS } from 'src/constants';
// 言語設定フックをインポート
import useLocales from 'src/hooks/useLocales';
import { usePermission } from 'src/hooks/usePermission';
import useSettings from 'src/hooks/useSettings';
// Redux ストアセレクターをインポート
import { RootState, useAppDispatch, useAppSelector } from 'src/redux/store';
// formatDateTime から dateFormatとformatterDateTime フックをインポート
import { dateFormat, formatterDateTime } from 'src/utils/formatDateTime';
// labelUtils から switchLabel フックをインポート
import { switchLabel } from 'src/utils/labelUtils';
// リマインドユーザーを選択するダイアログをインポート
import { SelectReminderUserModalLayout } from '../../components/SelectReminderUserModalLayout';
// リマインド日付を変更するダイアログをインポート
import useAuth from 'src/hooks/useAuth';
import { UpdateSentAtModalLayout } from '../../components/UpdateSentAtModalLayout';
import { ReminderEntity, useReminderListGrid } from '../../hooks/useReminderListGrid';
import { enqueteCalendarSlice, setReminderListData } from '../../store/enqueteCalendarSlice';

export const ReminderListGrid = () => {
  const calendarData = useAppSelector((state: RootState) => state.enqueteCalendar.calendarData);
  const isConfirmor = useAppSelector((state: RootState) => state.enqueteCalendar.isConfirmor);
  const reminderListData = useAppSelector((state: RootState) => state.enqueteCalendar.reminderListData);
  const isDestroyed = useAppSelector((state) => state.enqueteCalendar.isDestroyed);
  const [isDateModalOpen, setIsDateModalOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [reminderDate, setDeminderDate] = useState<string>('');

  const dispatch = useAppDispatch();
  const { isLoading } = useReminderListGrid();
  const { t } = useTranslation();
  const { currentLang } = useLocales();
  const storageKey = useAppSelector((state) => state.enqueteCalendar.storageKey);

  const [selectedId, setSelectedId] = useState<string>('');

  const { isAdmin, isViewerAdmin, isSurveyOwner, isAnswerEditor, isAnswerReader, isRespondentPerson, isRespondentManager } = usePermission();
  const [isDisabled, setIsDisabled] = useState(false);
  const { user } = useAuth();
  useEffect(() => {
    const isSystemCreated = calendarData.createdBy === CREATE_USER_SYSTEM;
    const isCreatedByMe = calendarData.createdBy === user?.email;

    // 共有権限
    const hasPermission =
      isSurveyOwner || // 管理者
      isAdmin || // admin
      isViewerAdmin || // viewer admin
      isAnswerEditor || // 本部極担当者（編集）
      isRespondentPerson || // 回答者
      isConfirmor || // 確認者
      !!calendarData?.reminderUserId; // リマインドの宛先

    // 手動権限
    const hasManualPermission =
      isAnswerReader || // 本部極担当者（参照）
      isRespondentManager || // 依頼先管理者
      !calendarData?.id || // 新規作成
      isCreatedByMe; // 個人作成

    const canEdit = isSystemCreated ? hasPermission : hasPermission || hasManualPermission;

    setIsDisabled(!canEdit);
  }, [calendarData, isAdmin, isViewerAdmin, isSurveyOwner, isAnswerEditor, isAnswerReader, isRespondentPerson, isConfirmor]);

  // テーマ設定
  const { themeMode } = useSettings();
  const isLight = themeMode === 'light';

  // ColDef読み込み
  const coldef: (ColDef | ColGroupDef)[] = [
    {
      field: currentLang.value === 'ja' ? 'reminderNameJpn' : 'reminderNameEng',
      headerName: t('CalendarCreate.gridColDef.reminderRecipient'),
      headerTooltip: t('CalendarCreate.gridColDef.reminderRecipient'),
      tooltipField: currentLang.value === 'ja' ? 'reminderNameJpn' : 'reminderNameEng',
      minWidth: 200,
      filter: false,
      filterParams: {
        suppressAndOrCondition: true,
        applyMiniFilterWhileTyping: true,
        filterOptions: ['contains'],
      },
      floatingFilter: false,
      valueGetter: (params) => {
        return switchLabel(params.data.reminderNameJpn ?? '', params.data.reminderNameEng ?? '', currentLang.value === 'ja');
      },
    },
    {
      field: 'sentAt',
      headerName: t('CalendarCreate.gridColDef.reminderDate'),
      headerTooltip: t('CalendarCreate.gridColDef.reminderDate'),
      valueGetter: (params) => {
        return formatterDateTime(params.data.sentAt, dateFormat) as string;
      },
      minWidth: 200,
      filter: false,
      floatingFilter: false,
    },
    {
      field: 'status',
      headerName: t('CalendarCreate.gridColDef.status'),
      headerTooltip: t('CalendarCreate.gridColDef.status'),
      valueGetter: (params) => {
        if (params.data.status === REMINDER_STATUS.ACTIVE) {
          return t('CalendarCreate.ReminderStatus.active');
        } else if (params.data.status === REMINDER_STATUS.SENT) {
          return t('CalendarCreate.ReminderStatus.sent');
        } else if (params.data.status === REMINDER_STATUS.FAILED) {
          return t('CalendarCreate.ReminderStatus.failed');
        } else if (params.data.status === REMINDER_STATUS.SKIP) {
          return t('CalendarCreate.ReminderStatus.skip');
        }
      },
      minWidth: 120,
      filter: false,
      floatingFilter: false,
    },
    {
      field: '-',
      headerName: '',
      cellRenderer: ThreeDotMenuRenderer,
      editable: false,
      minWidth: 80,
      width: 50,
      filter: false,
      pinned: 'right',
    },
  ];

  // リマインドユーザー追加：リマインド依頼を開く
  const add = async () => {
    setIsModalOpen(true);
  };

  //コンテキストメニュー
  const contextMenu: GetContextMenuItems = (params: GetContextMenuItemsParams) => {
    if (params.node === null) return [];
    // システムカレンダーである　かつ　ログインユーザーが本部極担当者（参照）　かつ 回答アクセスレベルがANSWER_READの場合
    // 回答アクセスレベルがANSWER_READ：可視条件.accessRoleId(アクセス権限のId)、アクセス権限.roleName(回答アクセスレベル)
    if (isDisabled) return [];

    const menuItems = [];
    // リマインド削除ボタン
    menuItems.push({
      name: t('CalendarCreate.contextMenu.delete'),
      action: () => {
        const selectId = params.node?.data.id;
        if (selectId !== '' && selectId !== undefined) {
          // 削除行Id：削除Idリストに追加
          const newDeleteRowIds = [...(calendarData?.deleteRowIds ?? []), params.node?.data.id];
          dispatch(
            enqueteCalendarSlice.actions.setCalendarData({
              ...calendarData,
              deleteRowIds: newDeleteRowIds,
            }),
          );
          // 更新行削除：表示一覧から削除
          const newListData: ReminderEntity[] = reminderListData.filter((item) => item.id !== params.node?.data.id);
          dispatch(setReminderListData(newListData));
        } else {
          // 新規行削除：表示一覧から削除
          const newListData: ReminderEntity[] = reminderListData.filter(
            (item) => item.addRowNum === undefined || item.addRowNum !== params.node?.data.addRowNum,
          );
          dispatch(setReminderListData(newListData));
        }
      },
    });
    // リマインドタイミング変更ボタン
    menuItems.push({
      name: t('CalendarCreate.contextMenu.update'),
      action: () => {
        const selectId = params.node?.data.id;
        setDeminderDate(params.node?.data.sentAt);
        if (selectId !== '' && selectId !== undefined) {
          // 更新行
          setSelectedId(params.node?.data.id);
        } else {
          // 新規行
          setSelectedId('addRowNum_' + params.node?.data.addRowNum);
        }
        // ブラウザの次回描画タイミングに合わせて処理を実行するための API
        requestAnimationFrame(() => {
          // リマインド日付変更ダイアログを開く
          setIsDateModalOpen(true);
        });
      },
    });

    return menuItems;
  };

  // ダブルクリック時の挙動定義
  const onRowDoubleClickedCallback = (event: RowDoubleClickedEvent) => {
    // システムカレンダーである　かつ　ログインユーザーが本部極担当者（参照）　かつ 回答アクセスレベルがANSWER_READの場合
    // 回答アクセスレベルがANSWER_READ：可視条件.accessRoleId(アクセス権限のId)、アクセス権限.roleName(回答アクセスレベル)
    if (isDisabled) return [];
    setDeminderDate(event.data?.sentAt);
    const selectId = event.data?.id;
    if (selectId !== '' && selectId !== undefined) {
      // 更新行
      setSelectedId(event.data?.id);
    } else {
      // 新規行
      setSelectedId('addRowNum_' + event.data?.addRowNum);
    }
    // ブラウザの次回描画タイミングに合わせて処理を実行するための API
    requestAnimationFrame(() => {
      // リマインド日付変更ダイアログを開く
      setIsDateModalOpen(true);
    });
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid container item alignItems="center" justifyContent="flex-start">
          <Grid item xs={3} sx={{ mt: 2 }}>
            <Typography variant="subtitle1">{t('CalendarCreate.reminderStatus')}</Typography>
          </Grid>
          {/* リマインドユーザー追加ボタン */}
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}>
            <Button
              // 塗りつぶし
              variant={'contained'}
              // クリックイベント
              onClick={() => {
                add();
              }}
              // 子要素
              children={t('CalendarCreate.button.reminderAdd')}
              sx={{ ml: 3 }}
              type="submit"
              disabled={isDisabled}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={12} md={12}>
          <Box
            className={isLight ? 'ag-theme-alpine' : 'ag-theme-alpine-dark'}
            sx={{
              height: '40vh',
              width: '100%',
              mt: 1,
            }}>
            <LoadableComponent isLoading={isLoading}>
              {!isDestroyed && (
                <StyledAgGrid
                  // 列定義
                  coldef={coldef}
                  // 表示する行情報
                  rows={reminderListData}
                  // 右クリック時のメニュー表示
                  contextMenu={contextMenu}
                  // 保存キー
                  storageKey={storageKey}
                  // 行ダブルクリック後に実行する関数
                  onRowDoubleClickedCallback={onRowDoubleClickedCallback}
                />
              )}
            </LoadableComponent>
          </Box>
        </Grid>
      </Grid>
      {/** リマインド日付変更モーダル */}
      <UpdateSentAtModalLayout
        // リマインド日付
        reminderDate={reminderDate}
        // リマインドID
        selectedId={selectedId}
        // ダイアログの開閉状態保持 (true：開く、false：閉じる)
        isModalOpen={isDateModalOpen}
        // ダイアログクローズ時動作。openをfalseに変更
        onClose={() => setIsDateModalOpen(false)}
      />
      {/** リマインド依頼モーダル */}
      <SelectReminderUserModalLayout
        // リマインドID
        reminderId={selectedId}
        // ダイアログの開閉状態保持 (true：開く、false：閉じる)
        isModalOpen={isModalOpen}
        // ダイアログクローズ時動作。openをfalseに変更
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};
